import React from 'react';
import {
  Mail,
  Send,
  ShieldCheck,
  Lock,
  MessageCircle,
  ListChecks,
  Sparkles,
  Globe,
  HeartHandshake,
  Clock,
  ArrowRight,
  AlertTriangle,
} from 'lucide-react';
import useInView from '../hooks/useInView';

const CONTACT_EMAIL = 'contact@romindkids.tech';

const contactEmailBody = `Name:
Email:
Country:
Reason for contacting ROMIND KIDS:
Message:

If this is about testing, please include:
Child Age Group: 9-11 or 12-14
Device Type: Android / iOS / Both

Please do not include unnecessary sensitive child information.`;

const CONTACT_MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('ROMIND KIDS Contact Request')}&body=${encodeURIComponent(contactEmailBody)}`;
const TESTING_MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('ROMIND KIDS Testing Request')}&body=${encodeURIComponent(`Parent / Guardian Name:
Parent / Guardian Email:
Country:
Child Age Group: 9-11 or 12-14
Device Type: Android / iOS / Both
Main Interest Area:
Optional Message:
I confirm that I am a parent or legal guardian: Yes`)}`;

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">{children}</p>
);

const InfoCard = ({ title, children, icon: Icon, accent = 'purple', testId }) => {
  const accentClasses = {
    purple: 'bg-purple-500/15 border-purple-400/25 text-purple-300',
    cyan: 'bg-cyan-500/12 border-cyan-400/25 text-cyan-300',
    emerald: 'bg-emerald-500/12 border-emerald-400/25 text-emerald-300',
    rose: 'bg-rose-500/10 border-rose-400/20 text-rose-300',
    amber: 'bg-amber-500/10 border-amber-400/20 text-amber-300',
  };

  return (
    <div data-testid={testId} className="glass-card p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${accentClasses[accent]}`}>
          <Icon size={16} />
        </div>
        <h3 className="font-outfit font-semibold text-white text-base">{title}</h3>
      </div>
      <div className="text-white/55 text-sm leading-relaxed">{children}</div>
    </div>
  );
};

const StepCard = ({ number, title, children, icon: Icon }) => (
  <div className="glass-card p-5 sm:p-6 border border-white/10 hover:border-purple-400/25 transition-all duration-300">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-xl bg-purple-500/15 border border-purple-400/25 flex items-center justify-center">
        {Icon ? <Icon size={16} className="text-purple-300" /> : <span className="text-purple-300 text-sm font-bold">{number}</span>}
      </div>
      <div>
        <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-white/35">Step {number}</p>
        <h3 className="font-outfit font-semibold text-white text-base">{title}</h3>
      </div>
    </div>
    <div className="text-white/55 text-sm leading-relaxed">{children}</div>
  </div>
);

const ContactTopic = ({ children, icon: Icon }) => (
  <div className="p-4 rounded-2xl bg-white/4 border border-white/8 flex items-start gap-3">
    <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-400/20 flex items-center justify-center flex-shrink-0">
      <Icon size={15} className="text-cyan-300" />
    </div>
    <p className="text-white/60 text-sm leading-relaxed">{children}</p>
  </div>
);

const Contact = () => {
  const contactTopics = [
    { icon: Sparkles, text: 'Testing requests and early access interest' },
    { icon: MessageCircle, text: 'Parent questions about ROMIND KIDS' },
    { icon: ShieldCheck, text: 'Child safety concerns or safety-related questions' },
    { icon: Lock, text: 'Privacy, data, and deletion requests' },
    { icon: HeartHandshake, text: 'Product feedback, app design feedback, clubs, and challenges feedback' },
    { icon: Globe, text: 'Partnership, press, Google Play, or general communication' },
  ];

  const messageDetails = [
    'Your name',
    'Your email address',
    'Your country',
    'Reason for contacting ROMIND KIDS',
    'A short message explaining your request',
    'If the message is about testing: child age group and device type',
    'If the message is about privacy or safety: enough detail for us to understand the request, without unnecessary sensitive child information',
  ];

  return (
    <main className="bg-[#05050A] min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute top-1/3 left-1/3 w-80 h-80 bg-purple-600/15 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute top-24 right-10 w-40 h-40 bg-pink-500/10 rounded-full blur-[80px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <SectionLabel>Contact ROMIND KIDS</SectionLabel>
            <h1 className="font-outfit font-black text-5xl sm:text-6xl text-white mb-5 tracking-tight leading-[1.05] max-w-4xl">
              Let’s talk about<br />
              <span className="gradient-text">a safer creative universe.</span>
            </h1>
            <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-3xl mb-8">
              ROMIND KIDS is currently a founder-operated project preparing for early preview and testing. For now, all contact requests are handled directly by email so every message can be reviewed carefully.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <a
                data-testid="contact-hero-email-btn"
                href={CONTACT_MAILTO}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm bg-purple-600/85 border border-purple-400/40 text-white hover:bg-purple-500 transition-all shadow-lg shadow-purple-900/30"
              >
                <Send size={15} />
                Email ROMIND KIDS
              </a>
              <a
                data-testid="contact-email-link"
                href={`mailto:${CONTACT_EMAIL}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/12 text-cyan-300 hover:bg-cyan-500/10 hover:border-cyan-300/40 transition-all"
              >
                <Mail size={15} />
                {CONTACT_EMAIL}
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Contact instructions */}
      <section data-testid="contact-email-instructions" className="py-12 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <FadeIn className="lg:col-span-3">
            <div className="glass-card p-6 sm:p-8 border border-purple-500/15">
              <SectionLabel>How to Contact Us</SectionLabel>
              <h2 className="font-outfit font-black text-3xl sm:text-4xl text-white tracking-tight mb-4">
                Send your message to<br />
                <a href={`mailto:${CONTACT_EMAIL}`} className="gradient-text break-all">
                  {CONTACT_EMAIL}
                </a>
              </h2>
              <p className="text-white/55 text-sm sm:text-base leading-relaxed max-w-2xl mb-8">
                Instead of using a backend form at this early stage, ROMIND KIDS accepts messages by email. This keeps the website lightweight, stable, and easy to review before the first public preview.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <StepCard number="1" title="Choose your topic" icon={MessageCircle}>
                  Tell us whether your message is about testing, parent questions, privacy, safety, product feedback, partnerships, press, or general communication.
                </StepCard>

                <StepCard number="2" title="Write the email" icon={Mail}>
                  Send your message to <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">{CONTACT_EMAIL}</a>. Use a clear subject so we can understand the request quickly.
                </StepCard>

                <StepCard number="3" title="Include useful details" icon={ListChecks}>
                  Add your name, email, country, reason for contacting us, and a short message. If your message is about testing, include the child age group and device type.
                </StepCard>

                <StepCard number="4" title="Wait for our reply" icon={Clock}>
                  We will review incoming messages and reply when possible. For testing requests, selected parents or guardians will be contacted when testing opportunities become available.
                </StepCard>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  data-testid="contact-email-main"
                  href={CONTACT_MAILTO}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full font-semibold text-sm bg-purple-600/85 border border-purple-400/40 text-white hover:bg-purple-500 transition-all shadow-lg shadow-purple-900/30"
                >
                  <Send size={14} />
                  Email ROMIND KIDS
                </a>
                <a
                  data-testid="contact-testing-email"
                  href={TESTING_MAILTO}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full font-semibold text-sm bg-white/5 border border-cyan-400/25 text-cyan-300 hover:bg-cyan-500/10 hover:border-cyan-300/50 transition-all"
                >
                  Testing Request <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={150} className="lg:col-span-2">
            <div className="space-y-4">
              <InfoCard title="Direct email" icon={Mail} accent="purple">
                <a href={`mailto:${CONTACT_EMAIL}`} className="font-outfit text-lg font-bold text-cyan-300 hover:text-cyan-200 transition-colors break-all">
                  {CONTACT_EMAIL}
                </a>
                <p className="mt-2 text-white/45 text-xs leading-relaxed">
                  Use this address for contact messages, testing requests, privacy questions, safety questions, and partnership inquiries.
                </p>
              </InfoCard>

              <InfoCard title="Safety note" icon={AlertTriangle} accent="amber">
                <p>
                  Please do not include unnecessary sensitive child data such as a child’s full name, school name, exact address, phone number, or precise location.
                </p>
              </InfoCard>

              <InfoCard title="Project status" icon={ShieldCheck} accent="emerald" testId="contact-project-status">
                <p className="mb-2">
                  ROMIND KIDS is currently a founder-operated project / private individual project, with plans to move toward the United Arab Emirates as the future operating jurisdiction.
                </p>
                <p>
                  Owner / Operator: <span className="text-white/75 font-medium">Svetlana Lukianova &amp; Sergey Lukianov</span>.
                </p>
              </InfoCard>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Contact topics */}
      <section data-testid="contact-topics-section" className="py-16 bg-[#080714]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <SectionLabel>What You Can Write About</SectionLabel>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight">
              Clear topics help us answer better.
            </h2>
            <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-2xl mb-8">
              ROMIND KIDS welcomes parent questions, early testing requests, safety concerns, product feedback, and partnership messages.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {contactTopics.map((topic, i) => (
              <FadeIn key={topic.text} delay={i * 60}>
                <ContactTopic icon={topic.icon}>{topic.text}</ContactTopic>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* What to include */}
      <section data-testid="contact-details-section" className="relative py-16 overflow-hidden">
        <div className="absolute top-1/2 left-1/4 w-80 h-80 bg-fuchsia-600/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="glass-card p-6 sm:p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div>
                  <SectionLabel>Message Checklist</SectionLabel>
                  <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight">
                    What to include in your email.
                  </h2>
                  <p className="text-white/50 text-sm sm:text-base leading-relaxed">
                    A clear message helps us understand the request and contact you back without collecting unnecessary information through the website.
                  </p>
                </div>

                <div className="space-y-3">
                  {messageDetails.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-purple-500/20 border border-purple-500/35 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-300" />
                      </div>
                      <p className="text-white/60 text-sm leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="contact-final-cta" className="relative py-20 bg-[#080714] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/10 via-transparent to-cyan-900/10" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-purple-500/12 border border-purple-400/25 mb-6">
              <HeartHandshake size={28} className="text-purple-300" />
            </div>

            <SectionLabel>Stay Connected</SectionLabel>
            <h2 className="font-outfit font-black text-4xl sm:text-5xl text-white mb-5 tracking-tight leading-tight">
              Have a question, idea,<br />
              or testing request?
            </h2>

            <p className="text-white/58 text-base sm:text-lg leading-relaxed mb-8 max-w-3xl mx-auto">
              Write to ROMIND KIDS directly. Whether you are a parent, partner, reviewer, or early supporter, your message helps us prepare the first public preview with more clarity and care.
            </p>

            <a
              data-testid="contact-final-email-btn"
              href={CONTACT_MAILTO}
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm bg-purple-600/85 border border-purple-400/40 text-white hover:bg-purple-500 transition-all shadow-lg shadow-purple-900/30"
            >
              Email ROMIND KIDS <ArrowRight size={15} />
            </a>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default Contact;
